// src/features/settings/SettingsPage.tsx
// Admin / Reception settings page with sectioned configuration

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '../../api/services';
import type { AppSettingDto } from '../../api/types';
import toast from 'react-hot-toast';
import {
  Clock, Timer, Building2, Bell, Save,
  Loader2, RefreshCw, Users, Briefcase
} from 'lucide-react';
import clsx from 'clsx';

/* ─── Section definitions ─────────────────────────────────────────── */

interface Section {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  category: string;
}

const SECTIONS: Section[] = [
  { id: 'booking', label: 'Booking Slots', icon: Clock, category: 'Booking' },
  { id: 'doctors', label: 'Doctors', icon: Users, category: 'Doctors' },
  { id: 'session', label: 'Session Timeout', icon: Timer, category: 'Session' },
  { id: 'branding', label: 'Branding', icon: Building2, category: 'Branding' },
  { id: 'notifications', label: 'Notifications', icon: Bell, category: 'Notification' },
  { id: 'management', label: 'Management Report', icon: Briefcase, category: 'Management' },
];

/* ─── Component ───────────────────────────────────────────────────── */

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('booking');
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  /* Fetch all settings */
  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: settingsService.getAll,
  });

  /* Populate form when data arrives */
  useEffect(() => {
    if (settings) {
      const vals: Record<string, string> = {};
      settings.forEach((s) => {
        vals[s.key] = s.value;
      });
      setFormValues(vals);
    }
  }, [settings]);

  /* Detect dirty state */
  const isDirty = useMemo(() => {
    if (!settings) return false;
    return settings.some((s) => formValues[s.key] !== s.value);
  }, [settings, formValues]);

  /* Update a single value */
  const setValue = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  /* Save mutation */
  const saveMutation = useMutation({
    mutationFn: () => {
      const changed = settings!
        .filter((s) => formValues[s.key] !== s.value)
        .map((s) => ({ key: s.key, value: formValues[s.key] }));
      return settingsService.bulkUpdate({ settings: changed });
    },
    onSuccess: () => {
      toast.success('Settings saved successfully');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to save settings');
    },
  });

  /* Helper to get a setting's description */
  const getDesc = (key: string): string =>
    settings?.find((s) => s.key === key)?.description ?? '';

  const getLastUpdated = (key: string): AppSettingDto | undefined =>
    settings?.find((s) => s.key === key);

  /* ─── Render ────────────────────────────────────────────────────── */

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">App Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage application configuration and preferences
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })}
            className="btn btn-ghost btn-sm"
            title="Reload settings"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
            className="btn btn-primary"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="ml-2">Save Changes</span>
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <nav className="w-52 shrink-0">
          <div className="card p-2 space-y-0.5">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={clsx(
                    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    activeSection === section.id
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1">
          <div className="card p-6">
            {activeSection === 'booking' && (
              <BookingSection
                formValues={formValues}
                setValue={setValue}
                getDesc={getDesc}
                getLastUpdated={getLastUpdated}
              />
            )}
            {activeSection === 'doctors' && <DoctorsSection />}
            {activeSection === 'session' && (
              <SessionSection
                formValues={formValues}
                setValue={setValue}
                getDesc={getDesc}
                getLastUpdated={getLastUpdated}
              />
            )}
            {activeSection === 'branding' && (
              <BrandingSection
                formValues={formValues}
                setValue={setValue}
                getDesc={getDesc}
                getLastUpdated={getLastUpdated}
              />
            )}
            {activeSection === 'notifications' && (
              <NotificationSection
                formValues={formValues}
                setValue={setValue}
                getDesc={getDesc}
                getLastUpdated={getLastUpdated}
              />
            )}
            {activeSection === 'management' && (
              <ManagementSection
                formValues={formValues}
                setValue={setValue}
                getDesc={getDesc}
                getLastUpdated={getLastUpdated}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Section Props ───────────────────────────────────────────────── */

interface SectionProps {
  formValues: Record<string, string>;
  setValue: (key: string, value: string) => void;
  getDesc: (key: string) => string;
  getLastUpdated: (key: string) => AppSettingDto | undefined;
}

/* ─── Booking Section ─────────────────────────────────────────────── */

function BookingSection({ formValues, setValue, getDesc }: SectionProps) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">Booking Slot Cutoffs</h2>
      <p className="text-sm text-slate-500 mb-6">
        Control when today's appointment slots stop appearing for patients.
        Slots past the cutoff hour will be hidden from the booking page.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SettingField
          label="Morning Cutoff Hour"
          description={getDesc('MorningCutoffHour') || 'Morning slots are hidden after this hour (24h format)'}
          value={formValues['MorningCutoffHour'] ?? '12'}
          onChange={(v) => setValue('MorningCutoffHour', v)}
          type="number"
          min={0}
          max={23}
          suffix="(0–23)"
        />
        <SettingField
          label="Evening Cutoff Hour"
          description={getDesc('EveningCutoffHour') || 'Evening slots are hidden after this hour (24h format)'}
          value={formValues['EveningCutoffHour'] ?? '15'}
          onChange={(v) => setValue('EveningCutoffHour', v)}
          type="number"
          min={0}
          max={23}
          suffix="(0–23)"
        />
      </div>

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>How it works:</strong> If Morning Cutoff is set to 12, then after 12:00 PM today,
          patients will no longer see morning slots for today. Evening slots follow the same logic
          with their cutoff hour.
        </p>
      </div>

      <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-1">Token Management</h2>
      <p className="text-sm text-slate-500 mb-4">
        Control whether token numbers are generated and whether they are shared with patients in notifications.
      </p>
      <div className="space-y-3">
        <ToggleField
          label="Token Number Generation"
          description={getDesc('TokenEnabled') || 'Generate sequential token numbers per doctor/session for internal queue management'}
          checked={formValues['TokenEnabled'] === 'true'}
          onChange={(v) => {
            setValue('TokenEnabled', v ? 'true' : 'false');
            // If generation is turned off, also disable notify
            if (!v) setValue('TokenNotifyEnabled', 'false');
          }}
        />
        <ToggleField
          label="Send Token Number in Notification"
          description={
            formValues['TokenEnabled'] !== 'true'
              ? 'Enable Token Generation first to use this setting'
              : (getDesc('TokenNotifyEnabled') || 'Include the token number in WhatsApp and email notifications sent to patients')
          }
          checked={formValues['TokenEnabled'] === 'true' && formValues['TokenNotifyEnabled'] === 'true'}
          onChange={(v) => setValue('TokenNotifyEnabled', v ? 'true' : 'false')}
          disabled={formValues['TokenEnabled'] !== 'true'}
        />
      </div>
      {formValues['TokenEnabled'] === 'true' && formValues['TokenNotifyEnabled'] !== 'true' && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Internal use only:</strong> Token numbers will be generated and visible to staff in the queue, but will not appear in patient WhatsApp or email notifications.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Doctors Section ─────────────────────────────────────────────── */

function DoctorsSection() {
  const navigate = useNavigate();
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">Doctor Management</h2>
      <p className="text-sm text-slate-500 mb-6">
        Add, edit, and manage doctor profiles, schedules, and leave entries.
      </p>

      <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg text-center">
        <Users className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-slate-700 mb-1">
          Full doctor management
        </h3>
        <p className="text-xs text-slate-500 mb-4 max-w-md mx-auto">
          Manage doctor profiles, weekly schedules, per-session settings (max patients,
          slot duration), and leave calendar from a dedicated admin page.
        </p>
        <button onClick={() => navigate('/admin/doctors')} className="btn btn-primary">
          Open Doctor Management
        </button>
      </div>
    </div>
  );
}

/* ─── Session Section ─────────────────────────────────────────────── */

function SessionSection({ formValues, setValue, getDesc }: SectionProps) {
  const timeout = Number(formValues['TimeoutSeconds'] ?? 900);
  const warning = Number(formValues['WarningSeconds'] ?? 60);

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">Session Timeout</h2>
      <p className="text-sm text-slate-500 mb-6">
        Configure how long users can remain inactive before being automatically logged out.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SettingField
          label="Inactivity Timeout"
          description={getDesc('TimeoutSeconds') || 'Seconds of inactivity before auto-logout'}
          value={formValues['TimeoutSeconds'] ?? '900'}
          onChange={(v) => setValue('TimeoutSeconds', v)}
          type="number"
          min={60}
          max={7200}
          suffix={`(${Math.floor(timeout / 60)} min)`}
        />
        <SettingField
          label="Warning Before Timeout"
          description={getDesc('WarningSeconds') || 'Seconds before timeout to show warning modal'}
          value={formValues['WarningSeconds'] ?? '60'}
          onChange={(v) => setValue('WarningSeconds', v)}
          type="number"
          min={10}
          max={300}
          suffix={`(${warning} sec)`}
        />
      </div>

      {warning >= timeout && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            Warning seconds must be less than the timeout seconds.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Branding Section ────────────────────────────────────────────── */

function BrandingSection({ formValues, setValue, getDesc }: SectionProps) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">App Branding</h2>
      <p className="text-sm text-slate-500 mb-6">
        Customize the hospital name displayed throughout the application.
      </p>

      <div className="max-w-md">
        <SettingField
          label="Hospital Name"
          description={getDesc('HospitalName') || 'Displayed in the app header and notifications'}
          value={formValues['HospitalName'] ?? 'RMV Hospital'}
          onChange={(v) => setValue('HospitalName', v)}
          type="text"
        />
      </div>

      {/* Live preview */}
      <div className="mt-6">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Preview</p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-700 rounded-lg">
          <span className="text-sm font-semibold text-white">
            {formValues['HospitalName'] || 'RMV Hospital'}
          </span>
          <span className="text-xs text-brand-200">· Appointment System</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Notification Section ────────────────────────────────────────── */

function NotificationSection({ formValues, setValue, getDesc }: SectionProps) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">Notification Settings</h2>
      <p className="text-sm text-slate-500 mb-6">
        Control which notification channels are active for patient communications.
      </p>

      <div className="space-y-4">
        <ToggleField
          label="WhatsApp Notifications"
          description={getDesc('WhatsAppEnabled') || 'Send appointment updates via WhatsApp'}
          checked={formValues['WhatsAppEnabled'] === 'true'}
          onChange={(v) => setValue('WhatsAppEnabled', v ? 'true' : 'false')}
        />
        <ToggleField
          label="Email Notifications"
          description={getDesc('EmailEnabled') || 'Send appointment updates via email'}
          checked={formValues['EmailEnabled'] === 'true'}
          onChange={(v) => setValue('EmailEnabled', v ? 'true' : 'false')}
        />
      </div>
    </div>
  );
}

/* ─── Management Report Section ───────────────────────────────────── */

function ManagementSection({ formValues, setValue, getDesc }: SectionProps) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">Management Daily Report</h2>
      <p className="text-sm text-slate-500 mb-6">
        Configure daily summary reports sent to hospital management with yesterday's
        appointment statistics broken down by source (Website, On-Call, Walk-In, etc.).
      </p>

      <ToggleField
        label="Management Briefing"
        description={getDesc('ManagementBriefingEnabled') || 'Send daily summary report to management contacts at 7:00 AM'}
        checked={formValues['ManagementBriefingEnabled'] !== 'false'}
        onChange={(v) => setValue('ManagementBriefingEnabled', v ? 'true' : 'false')}
      />

      <div className="mt-6 space-y-4">
        <div>
          <label className="label">Management Email Addresses</label>
          <input
            className="input w-full"
            placeholder="email1@hospital.com, email2@hospital.com"
            value={formValues['ManagementEmails'] ?? ''}
            onChange={(e) => setValue('ManagementEmails', e.target.value)}
          />
          <p className="text-xs text-slate-400 mt-1">
            {getDesc('ManagementEmails') || 'Comma-separated email addresses. First email is the primary recipient, others are CC.'}
          </p>
        </div>

        <div>
          <label className="label">Management WhatsApp Numbers</label>
          <input
            className="input w-full"
            placeholder="9880848262, 9876543210"
            value={formValues['ManagementPhones'] ?? ''}
            onChange={(e) => setValue('ManagementPhones', e.target.value)}
          />
          <p className="text-xs text-slate-400 mt-1">
            {getDesc('ManagementPhones') || 'Comma-separated phone numbers for WhatsApp daily summary (requires template setup in MSG91).'}
          </p>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Report includes:</strong> Total appointments, completed, pending, cancelled/rejected counts —
          broken down by booking source (Website, On-Call, WhatsApp, Walk-In). Sent daily at 7:00 AM with yesterday's data.
        </p>
      </div>
    </div>
  );
}

/* ─── Reusable Field Components ───────────────────────────────────── */

interface SettingFieldProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  type: 'text' | 'number';
  min?: number;
  max?: number;
  suffix?: string;
}

function SettingField({ label, description, value, onChange, type, min, max, suffix }: SettingFieldProps) {
  return (
    <div>
      <label className="label">
        {label} {suffix && <span className="text-slate-400 font-normal ml-1">{suffix}</span>}
      </label>
      <input
        type={type}
        className="input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
      />
      <p className="text-xs text-slate-400 mt-1">{description}</p>
    </div>
  );
}

interface ToggleFieldProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleField({ label, description, checked, onChange, disabled }: ToggleFieldProps) {
  return (
    <div className={clsx(
      'flex items-center justify-between p-4 rounded-lg border transition-opacity',
      disabled ? 'bg-slate-50/50 border-slate-100 opacity-50 cursor-not-allowed' : 'bg-slate-50 border-slate-200'
    )}>
      <div>
        <div className={clsx('text-sm font-medium', disabled ? 'text-slate-500' : 'text-slate-800')}>{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={clsx(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          checked && !disabled ? 'bg-brand-600' : 'bg-slate-300'
        )}
      >
        <span
          className={clsx(
            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm',
            checked && !disabled ? 'translate-x-6' : 'translate-x-1'
          )}
        />
      </button>
    </div>
  );
}
